<template>
  <div class="base-pagination">
    <div class="page-section">
      <div class="d-flex align-items-center">
        <button class="arrow-button" type="button" @click="onClickDoubleLeftArrowButton">
          <double-left-arrow-icon class="me-1" />
        </button>
        <button class="arrow-button" type="button" @click="onClickLeftArrowButton">
          <left-arrow-icon />
        </button>
        <ul class="page-list" v-if="totalPages <= 4">
          <li v-for="n in totalPages" :key="`page-${n}`">
            <button class="page" type="button" :class="{ selected: n == currentPage }" @click="onSelectPage(n)">{{ n }}</button>
          </li>
        </ul>
        <ul class="page-list" v-else-if="currentPage <= 3 && totalPages - currentPage >= 3">
          <li v-for="n in 4" :key="`page-${n}`">
            <button class="page" type="button" :class="{ selected: n == currentPage }" @click="onSelectPage(n)">{{ n }}</button>
          </li>
          <li>
            <div>{{ '...' }}</div>
          </li>
        </ul>
        <ul class="page-list" v-else-if="totalPages - currentPage >= 3">
          <li>
            <div>{{ '...' }}</div>
          </li>
          <li v-for="n in 3" :key="`page-${currentPage - 2 + n}`">
            <button class="page" type="button" :class="{ selected: currentPage - 2 + n == currentPage }" @click="onSelectPage(currentPage - 2 + n)">
              {{ currentPage - 2 + n }}
            </button>
          </li>
          <li>
            <div>{{ '...' }}</div>
          </li>
        </ul>
        <ul class="page-list" v-else-if="totalPages - currentPage < 3">
          <li>
            <div>{{ '...' }}</div>
          </li>
          <li v-for="n in 4" :key="`page-${totalPages - 4 + n}`">
            <button class="page" type="button" :class="{ selected: totalPages - 4 + n == currentPage }" @click="onSelectPage(totalPages - 4 + n)">
              {{ totalPages - 4 + n }}
            </button>
          </li>
        </ul>
        <button class="arrow-button" type="button" @click="onClickRightArrowButton">
          <right-arrow-icon class="me-1" />
        </button>
        <button class="arrow-button" type="button" @click="onClickDoubleRightArrowButton">
          <double-right-arrow-icon />
        </button>
      </div>
      <div class="pagination-info">
        {{ paginationInfo }}
      </div>
    </div>
    <div class="go-to-section ms-2">
      <div class="me-1 go-to">{{ t('component.pagination.go_to') }}</div>
      <div class="d-flex align-items-center">
        <div class="search-box">
          <input class="search-input" ref="inputBoxRef" @keydown="onKeyDown" @keyup="onKeyUp" />
          <button class="search-button" type="button" @click="onSearchPage">
            <search-icon />
          </button>
        </div>
        <div class="ms-2">{{ t('component.pagination.page') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import DoubleLeftArrowIcon from '@/assets/icons/base/pagination/DoubleLeftArrowIcon.vue';
import LeftArrowIcon from '@/assets/icons/base/pagination/LeftArrowIcon.vue';
import DoubleRightArrowIcon from '@/assets/icons/base/pagination/DoubleRightArrowIcon.vue';
import RightArrowIcon from '@/assets/icons/base/pagination/RightArrowIcon.vue';
import SearchIcon from '@/assets/icons/base/SearchIcon.vue';

import usePagination from './hooks/usePagination';

interface Props {
  modelValue: number;
  perPages?: number;
  totalRows?: number;
  listUnit?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 1,
  perPages: 10,
});

const { t } = useI18n();

const {
  currentPage,
  inputBoxRef,
  totalPages,
  paginationInfo,
  onSelectPage,
  onSearchPage,
  onKeyUp,
  onKeyDown,
  onClickDoubleLeftArrowButton,
  onClickLeftArrowButton,
  onClickDoubleRightArrowButton,
  onClickRightArrowButton,
} = usePagination(props);
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/pagination.scss';
</style>
